import React, { useCallback, useState } from 'react';
// @ts-ignore
import styles from './EArchiveToolbar.module.scss';
import { useAppContext } from '../../../../Services/AppContext';
import { Flyer } from '../../Enums/Flyer.enum';
import {
	useWorkplaceCodeQuery,
	useWorkplaceMemberIdQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import PatientManualRegistrationButton from '../PatientManualRegistration/PatientManualRegistrationButton';
import Text from '../../../../Components/Base/Text/Text';
import { View } from '../../../../Enums/View.enum';
import { joinClasses } from '../../../../Utils/className.util';
import EArchiveToolbarCode from './EArchiveToolbarCode';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import Button from '../../../../Components/Button/Button';
import useNotification from '../../../../Hooks/useNotification.hook';
import apiClient from '../../../../Services/ApiClient';

interface Props {
	itemsCount: number;
	onViewChange: (newState: View) => void;
	activeView: View;
	reloadData: () => void;
}

const EArchiveToolbar: React.FC<Props> = ({
	itemsCount,
	activeView,
	onViewChange,
	reloadData,
}) => {
	const { workplaceId, userData, isMou } = useAppContext();
	const { data, isLoading } = useWorkplaceMemberIdQuery({
		userId: userData.id,
		workplaceId,
	});
	const { data: workplaceCode, isLoading: workplaceCodeIsLoading } =
		useWorkplaceCodeQuery({ id: workplaceId });
	const { showNotificationSuccess, showNotificationAlert } = useNotification();
	const [isResendConfirmationOpen, setIsResendConfirmationOpen] =
		useState(false);

	const workplaceMember = data?.workplaceMember;

	const resendInvitations = useCallback(async () => {
		const result = await apiClient.post(
			`/workplaces/${workplaceId}/patient/invitation-resend-bulk`,
			{},
		);

		if (result?.status === 200) {
			showNotificationSuccess({
				content: 'component.invitations.sendSuccess',
			});
		} else {
			showNotificationAlert({
				content: 'component.invitations.sendFail',
			});
		}

		setIsResendConfirmationOpen(false);
	}, [workplaceId]);

	if (isLoading || workplaceCodeIsLoading) {
		return <Loading />;
	}

	if (!workplaceMember) {
		return <></>;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.leftSide}>
				<div className={styles.count}>
					<Text tx='base.numberOfRecords' firstUpper inline />: {itemsCount}
				</div>
			</div>
			<div className={styles.rightSide}>
				{!isMou && (
					<>
						<div className={styles.codes}>
							{workplaceCode?.workplace?.invitationCode && (
								<EArchiveToolbarCode
									code={workplaceCode.workplace.invitationCode}
									workplaceMemberId={workplaceMember.id}
									workplaceId={workplaceId}
									flyerType={Flyer.E_ARCHIVE_WORKPLACE}
									title='component.workplaceAndPatients.workplaceCode'
									tooltipText='component.workplaceAndPatients.workplaceCodeTooltip'
									buttonTitle='component.workplaceAndPatients.workplaceCodePrintButton'
								/>
							)}
							{workplaceMember.code && (
								<EArchiveToolbarCode
									code={workplaceMember.code}
									workplaceMemberId={workplaceMember.id}
									workplaceId={workplaceId}
									flyerType={Flyer.E_ARCHIVE}
									title='component.workplaceAndPatients.codeForPatients'
									tooltipText='component.workplaceAndPatients.codeTooltip'
								/>
							)}
						</div>
						<PatientManualRegistrationButton reloadData={reloadData} />
						<LinkButton
							title={'component.invitations.resend'}
							theme={Theme.BLUE}
							onClick={() => setIsResendConfirmationOpen(true)}
						/>
					</>
				)}
				{isMou && <PatientManualRegistrationButton reloadData={reloadData} />}
				<div className={styles.viewToggle}>
					<div
						className={joinClasses([
							styles.toggleGrid,
							activeView === View.GRID ? styles.active : undefined,
						])}
						onClick={() => onViewChange(View.GRID)}
					/>
					<div
						className={joinClasses([
							styles.toggleList,
							activeView === View.LIST ? styles.active : undefined,
						])}
						onClick={() => onViewChange(View.LIST)}
					/>
				</div>
			</div>
			{isResendConfirmationOpen && (
				<ConfirmationModal
					isOpen
					onClose={() => setIsResendConfirmationOpen(false)}
					title={'component.invitations.sendConfirmTitle'}
					text={'component.invitations.sendConfirmText'}>
					<Button
						theme={Theme.GREEN}
						onClick={resendInvitations}
						title={'base.shared.form.send'}
					/>
				</ConfirmationModal>
			)}
		</div>
	);
};

export default EArchiveToolbar;
